import React, { useState, useContext } from "react";
import {
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Button,
  Divider,
} from "@mui/material";
import { getColors } from "../../themes/theme";
import { ThemeContext } from "../../context/ThemeContext";
import { EditTreatmentDialog } from "./EditTreatmentDialog";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";

export const SelectedCustomTreatmentItem = ({
  id,
  name,
  onCheck,
  isChecked,
  onTreatmentUpdate,
}) => {
  const { token } = useAuthContext();
  const { darkMode } = useContext(ThemeContext);
  const [editTreatmentDialogOpen, setEditTreatmentDialogOpen] = useState(false);
  const [treatmentToEdit, setTreatmentToEdit] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  const handleCheckboxChange = (event) => {
    event.stopPropagation(); // Prevent the Box's onClick from being triggered
    onCheck(id, name, event.target.checked); // Notify parent about the change
  };

  const handleBoxClick = () => {
    onCheck(id, name, !isChecked); // Toggle the checked state
  };

  const handleEditTreatment = async (data) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/custom-treatment/${id}`,
        {
          ...data,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      onTreatmentUpdate(response.data);
      enqueueSnackbar("Successfully Changed!", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to update treatment!", { variant: "error" });
    }
  };

  const handleModifyClicked = async (e) => {
    e.stopPropagation();
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/custom-treatment/${id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    setTreatmentToEdit(response.data); // Set the treatment data to edit
    setEditTreatmentDialogOpen(true); // Open the edit dialog
  };

  const handleDeleteClicked = async (e) => {
    e.stopPropagation();
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/custom-treatment/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      onTreatmentUpdate({ id, deleted: true }); // Notify parent about the deletion
      enqueueSnackbar("Successfully Deleted!", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to delete treatment!", { variant: "error" });
    }
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          px: { xs: 2, sm: "20px", md: 2, lg: "20px" },
          py: "12px",
          borderRadius: "5px",
          borderWidth: 1,
          borderStyle: "solid",
          borderColor: darkMode
            ? getColors.thumbnailBorderDark
            : getColors.thumbnailBorderLight,
          background: darkMode
            ? getColors.thumbnnailBgDark
            : getColors.thumbnnailBgLight,
          cursor: "pointer",
          "&:hover": {
            borderColor: darkMode
              ? getColors.thumbnailTextDark
              : getColors.thumbnailTextLight,
          },
        }}
        onClick={handleBoxClick} // Trigger the same logic as the checkbox
      >
        <Box>
          <Box
            display={"flex"}
            justifyContent="space-between"
            sx={{ alignItems: "center" }}
          >
            <Typography
              sx={{
                fontSize: "20px",
                color: darkMode
                  ? getColors.drawerTextDark
                  : getColors.thumbnailTextLight,
                fontWeight: 500,
                textAlign: "left",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {name}
            </Typography>
            <FormControlLabel
              sx={{ margin: 0 }}
              control={
                <Checkbox
                  checked={isChecked} // Use the isChecked prop to control the checkbox state
                  onClick={handleCheckboxChange} // Allow manual toggling
                  sx={{
                    "&.Mui-checked": {
                      color: "inherit",
                    },
                  }}
                />
              }
            />
          </Box>
          <Box display={"flex"}>
            <Button
              variant="text"
              sx={{
                fontSize: { xs: "14px", sm: "15px", md: "16px" },
                color: darkMode
                  ? getColors.drawerTextDark
                  : getColors.thumbnailTextLight,
                "&:hover": {
                  background: "none",
                },
              }}
              onClick={handleModifyClicked}
            >
              Modify
            </Button>
            <Divider
              orientation="vertical"
              variant="middle"
              flexItem
              sx={{
                mx: { xs: 1, sm: "3px", md: 1 },
                bgcolor: darkMode
                  ? getColors.drawerTextDark
                  : getColors.thumbnailTextLight,
              }}
            />
            <Button
              variant="text"
              sx={{
                fontSize: { xs: "14px", sm: "15px", md: "16px" },
                color: darkMode
                  ? getColors.drawerTextDark
                  : getColors.thumbnailTextLight,
                "&:hover": { background: "none" },
              }}
              onClick={handleDeleteClicked}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Box>
      <EditTreatmentDialog
        open={editTreatmentDialogOpen}
        onClose={() => setEditTreatmentDialogOpen(false)}
        treatmentData={treatmentToEdit}
        onSave={handleEditTreatment}
      />
    </>
  );
};
